"use client";
import { useTranslations, useFormatter } from "next-intl";
import { useRef, useState, Fragment, useMemo, memo } from "react";
import Image from "next/image";
import clsx from "clsx";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Draggable } from "gsap/Draggable";

import useOpenData from "@/shared/utils/useOpenData";

import sweatLogo from "@public/sweat_logo_only.svg";
import stepsIcon from "@public/steps_black.svg";
import tactileMark from "@public/tactile_mark.svg";

import s from "./styles.module.css";

gsap.registerPlugin(useGSAP, Draggable);

const MIN_STEPS = 0;
const MAX_STEPS = 20000;
const MINIMAL_NON_REWARDED_STEPS = 3000;
const DEFAULT_STEPS = 10000;
const DEFAULT_PROGRESS = 0.5;
const PREMIUM_PIVOT = 10000 + 1;
const MINIMAL_PROGRESS = MINIMAL_NON_REWARDED_STEPS / MAX_STEPS;
const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

interface DigitProps {
  value: number;
  height: number;
  containerClassName?: string;
  slideClassName?: string;
}

const Digit = memo(function Digit({
  value,
  height,
  containerClassName,
  slideClassName,
}: DigitProps) {
  const displayValue = Math.max(0, Math.min(9, Math.floor(value)));
  return (
    <div
      className={clsx(containerClassName, s.digit)}
      style={{ transform: `translateY(${displayValue * (0 - height)}px)` }}
    >
      {DIGITS.map((digit) => (
        <div key={digit} className={clsx(slideClassName, s.decorativeCharacter)} data-character={digit} />
      ))}
    </div>
  );
});

interface DigitsWithDelimiterProps extends DigitProps {
  maxDigits?: number;
}

const formatToMaxDigits = (num: number, maxDigits: number): number => {
  const str = num.toString().replace(/[^\d.]/g, "");
  const [intPart, decPart = ""] = str.split(".");
  const allDigits = intPart + decPart;
  const trimmed = allDigits.slice(0, maxDigits);

  return intPart.length >= maxDigits
    ? parseFloat(trimmed)
    : parseFloat(
        `${trimmed.slice(0, intPart.length)}.${trimmed.slice(intPart.length)}`
      );
};

const DIGIT_REGEX = /\d/;

const DigitsWithDelimiter = memo(function DigitsWithDelimiter({
  maxDigits = 5,
  value,
  ...props
}: DigitsWithDelimiterProps) {
  const formatter = useFormatter();

  const formattedNumber = formatter.number(formatToMaxDigits(value, maxDigits));
  const chars = Array.from(formattedNumber);

  return (
    <>
      <span className={s.readableValue}>{formattedNumber}</span>
      <div className={s.sliderContainer} aria-hidden="true">
        {chars.map((char, index) => (
          <Fragment key={index}>
            {DIGIT_REGEX.test(char) ? (
              <Digit value={Number(char)} {...props} />
            ) : (
              <span className={clsx(props.slideClassName, s.decorativeCharacter)} data-character={char} />
            )}
          </Fragment>
        ))}
      </div>
    </>
  );
});

const getValue = (progress: number) => {
  return Math.floor(progress * (MAX_STEPS - MIN_STEPS) + MIN_STEPS);
};

export default function EarnSimulator() {
  const t = useTranslations("StepsInteractiveCalculator");
  const [progress, setProgress] = useState(DEFAULT_PROGRESS);
  const [externalValue, setExternalValue] = useState(DEFAULT_STEPS);

  const [isReady, setReady] = useState(false);

  const openData = useOpenData();

  const latestProgress = useRef(progress);
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const trackWrapperRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const container = containerRef.current;
      const thumb = thumbRef.current;

      if (!container || !thumb) {
        return;
      }

      const containerWidth = container.offsetWidth;

      const maxX = containerWidth - thumb.offsetWidth;
      const minX = maxX * MINIMAL_PROGRESS;

      gsap.set(thumb, { x: DEFAULT_PROGRESS * maxX });

      Draggable.create(thumb, {
        type: "x",
        bounds: {
          minX,
          maxX,
        },
        inertia: true,
        force3D: true,
        onDrag: function () {
          requestAnimationFrame(() => {
            const rawProgress = this.x / maxX;
            const newProgress = Math.max(
              MINIMAL_PROGRESS,
              Math.min(1, rawProgress)
            );
            setProgress(newProgress);
            latestProgress.current = newProgress;
          });
        },
        onDragEnd: function () {
          setExternalValue(getValue(latestProgress.current));
        },
      });

      setReady(true);
    },
    { scope: containerRef }
  );

  const perDay = useMemo(
    () =>
      openData?.oneStepPrice
        ? (externalValue - MINIMAL_NON_REWARDED_STEPS) * openData?.oneStepPrice
        : 0,
    [openData, externalValue]
  );

  const perYear = useMemo(() => (perDay ? perDay * 365 : 0), [perDay]);

  const value = Math.round(getValue(progress) / 1000) * 1000;
  const isPremium = value >= PREMIUM_PIVOT;

  return (
    <div>
      <div className={s.preview}>
        <div className={s.previewYear}>
          <h3 className={s.yearTitle}>
            <Image src={sweatLogo} className={s.yearLogo} alt="$SWEAT logo" />
            <DigitsWithDelimiter
              height={40}
              value={perYear}
              containerClassName={s.yearSlideContainer}
              slideClassName={s.yearTitle}
            />
          </h3>
          <p className={s.yearText}>{t("oneYearBalance")}</p>
        </div>
        <div className={s.previewDay}>
          <h3 className={s.dayTitle}>
            <Image src={sweatLogo} className={s.dayLogo} alt="$SWEAT logo" />
            <DigitsWithDelimiter
              height={24}
              value={perDay}
              containerClassName={s.daySlideContainer}
              slideClassName={s.dayTitle}
            />
          </h3>
          <p className={s.dayText}>{t("oneDayBalance")}</p>
        </div>
      </div>

      <p className={s.sliderHeadingTitle}>{t("sliderTitle")}</p>
      <div className={s.container}>
        <div
          className={clsx(
            s.backgroundBorder,
            isPremium && s.backgroundBorderPremium
          )}
        >
          <div ref={trackWrapperRef} className={s.trackBackground}>
            <div className={clsx(s.inputSliderSteps, isReady && s.stepsReady)}>
              <Image width={16} height={16} src={stepsIcon} alt="steps icon" />
              <DigitsWithDelimiter
                height={16}
                value={value}
                containerClassName={s.stepSlideContainer}
                slideClassName={s.stepSlideTitle}
              />
            </div>
            <div
              ref={containerRef}
              className={clsx(s.track, isPremium && s.trackPremium)}
            >
              <div ref={thumbRef} className={s.thumb}>
                <div className={s.progressFiller} />
                <Image src={tactileMark} alt="tactile mark" />
              </div>
            </div>
          </div>
          <div className={clsx(s.premium, isPremium && s.labelVisible)}>
            <span className={s.label}>{t("premiumLabel")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
